import React, { useEffect, useMemo, useRef, useState } from 'react';

import { HitType } from '@/components/Search/Hit.types';
import {
  VariantGrid,
  VariantGridItem,
  VariantGridItemSkeleton,
} from '@/components/VariantGrid';
import { useWindowSize } from '@/hooks/useWindowSize';
import {
  ProductList,
  sendGtmProductClickEvent,
  sendGtmProductImpressionsEvent,
} from '@/utils/sendGtmEvents';

import { ProductRecommendationsCarousel } from './ProductRecommendationsCarousel';
import { ProductRecommendationsTabs } from './ProductRecommendationsTabs';
import { useCategories } from './useCategories';
import useProductRecommendations from './useProductRecommendations';

type Props = {
  categoriesLvl0: string;
  categoriesLvl1: string;
  categoriesLvl2: string;
  sku: string;
  productName: string;
};

export const ProductRecommendations = ({
  categoriesLvl0,
  categoriesLvl1,
  sku,
  productName,
}: Props) => {
  const { width } = useWindowSize();
  const ref = useRef<HTMLDivElement>(null);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const { data: categoriesData, status: categoriesStatus } = useCategories(
    categoriesLvl0,
    categoriesLvl1,
  );

  const [inView, setInView] = React.useState<boolean>(false);
  const [hasSentImpressions, setHasSentImpressions] =
    React.useState<boolean>(false);

  const { data: productsData, status: productsStatus } =
    useProductRecommendations({
      sku,
      productName,
      currentCategory: categoriesLvl1,
      categories: categoriesData,
      hasDepartment: !!categoriesLvl0,
    });

  const isMd = width >= 768;

  const categories = useMemo(() => {
    return ['Related products', 'Bestsellers', ...(categoriesData || [])];
  }, [categoriesData]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      });
    });

    let current: HTMLDivElement | null = null;

    if (ref.current) {
      current = ref.current;
      observer.observe(ref.current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, []);

  useEffect(() => {
    if (inView && productsData?.at(0).length > 0 && !hasSentImpressions) {
      sendGtmProductImpressionsEvent({
        list: ProductList.RecommendedProducts,
        impressions: productsData?.at(0).map((hit: HitType, index: number) => ({
          sku: hit.sku,
          productName: hit.productName,
          variantName: hit.variantName,
          price: hit.price.toString(),
          originalPrice: (hit.compareAtPrice || hit.price)?.toString(),
          position: index,
          slug: hit.slug,
        })),
      });

      setHasSentImpressions(true);
    }
  }, [productsData, hasSentImpressions, inView]);

  return (
    <div ref={ref}>
      <ProductRecommendationsTabs
        status={categoriesStatus}
        categories={categories}
        onSelect={setSelectedCategoryIndex}
        selectedCategoryIndex={selectedCategoryIndex}
      />

      {productsStatus === 'pending' && (
        <div className="mb-4 mt-2">
          <VariantGrid>
            {Array.from({ length: isMd ? 4 : 2 }).map((_, index) => (
              <VariantGridItemSkeleton key={index} />
            ))}
          </VariantGrid>
        </div>
      )}

      {productsStatus === 'success' && (
        <ProductRecommendationsCarousel
          selectedIndex={selectedCategoryIndex}
          slides={productsData[selectedCategoryIndex].map(
            (hit: HitType, index: number) => (
              <VariantGridItem
                key={hit.sku}
                sku={hit.sku}
                slug={hit.slug}
                productName={hit.productName}
                variantName={hit.variantName}
                thumbnailImage={undefined}
                thumbnailImageUrl={hit.imageUrl}
                hoverImage={undefined}
                hoverImageUrl={hit.hoverImageUrl}
                shopify={{
                  price: hit.price,
                  compareAtPrice: hit.compareAtPrice,
                  inventoryQuantity: undefined,
                }}
                airtable={{
                  type: hit.type,
                  status: hit.status,
                  colorHex: Array.isArray(hit.productColors)
                    ? hit.productColors[0]
                    : undefined,
                  product: {
                    colorsHex: hit.productColors,
                  },
                }}
                onClick={() => {
                  sendGtmProductClickEvent({
                    sku: hit.sku,
                    productName: hit.productName,
                    variantName: hit.variantName,
                    price: hit.price + '',
                    originalPrice: (hit.compareAtPrice || hit.price) + '',
                    slug: hit.slug,
                    list: ProductList.RecommendedProducts,
                    position: index,
                  });
                }}
              />
            ),
          )}
        />
      )}
    </div>
  );
};
